import throttle from 'lodash/throttle';
import { useState, useEffect, type FC, ChangeEventHandler } from 'react';

import { InputTextProps } from '../../../types/InputText';

import styles from './InputTextWithSuggestions.module.scss';

import { assertNever } from '@/packages/nurse-senka-frontend-sdk';

const limitThreshold = 1000;

// eslint-disable-next-line max-lines-per-function
export const InputTextWithSuggestions: FC<InputTextProps> = ({
  type,
  width,
  placeholder,
  value,
  defaultValue,
  disable,
  onChange,
  fetchSuggestions,
  afterClickSuggestionText,
}) => {
  // eslint-disable-next-line no-undefined
  const [text, setText] = useState<string>(value || '');

  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    // eslint-disable-next-line no-undefined
    if (value !== undefined) {
      setText(value);
    }
  }, [value]);

  const onChangeWithSuggestions: ChangeEventHandler<HTMLInputElement> = async (
    event,
  ) => {
    const target = event.target.value;

    setText(target);

    // 親のonChangeハンドラーを呼び出す
    if (onChange) {
      onChange(event);
    }

    if (fetchSuggestions && target) {
      const fetchedSuggestions = await fetchSuggestions(target);
      setSuggestions(fetchedSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const throttledOnChangeWithSuggestions = throttle<
    typeof onChangeWithSuggestions
  >(onChangeWithSuggestions, limitThreshold);

  const onClickSuggestionText = async (suggestion: string) => {
    await setText(suggestion);
    await setSuggestions([]);

    if (afterClickSuggestionText) {
      await afterClickSuggestionText(suggestion);
    }
  };

  switch (width) {
    case 'default':
      return (
        <>
          {disable ? (
            <input
              className={styles['input-text-default-disable']}
              value={value}
              defaultValue={defaultValue}
              placeholder={placeholder}
              type={type}
              onChange={onChange}
            />
          ) : (
            <>
              <input
                className={styles['input-text-default']}
                type={type}
                onChange={throttledOnChangeWithSuggestions}
                placeholder={placeholder}
                value={text}
              />
              {/* eslint-disable-next-line no-magic-numbers */}
              {suggestions.length !== 0 && (
                <div className={styles['suggestion-box']}>
                  {suggestions?.map((suggestion, index) => (
                    <button
                      className={styles['suggestion-text']}
                      key={index}
                      onClick={() => onClickSuggestionText(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      );
    case 'wide':
      return (
        <>
          {disable ? (
            <input
              className={styles['input-text-wide-disable']}
              value={value}
              defaultValue={defaultValue}
              placeholder={placeholder}
              type={type}
              onChange={onChange}
            />
          ) : (
            <>
              <input
                className={styles['input-text-wide']}
                type={type}
                value={text}
                onChange={throttledOnChangeWithSuggestions}
                placeholder={placeholder}
              />
              {/* eslint-disable-next-line no-magic-numbers */}
              {suggestions.length !== 0 && (
                <div className={styles['suggestion-box']}>
                  {suggestions?.map((suggestion, index) => (
                    <button
                      className={styles['suggestion-text']}
                      key={index}
                      onClick={() => onClickSuggestionText(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      );
    case 'short':
      return (
        <>
          {disable ? (
            <input
              className={styles['input-text-short-disable']}
              value={text}
              defaultValue={defaultValue}
              placeholder={placeholder}
              type={type}
              onChange={onChange}
            />
          ) : (
            <>
              <input
                className={styles['input-text-short']}
                value={text}
                defaultValue={defaultValue}
                placeholder={placeholder}
                type={type}
                onChange={throttledOnChangeWithSuggestions}
              />
              {/* eslint-disable-next-line no-magic-numbers */}
              {suggestions.length !== 0 && (
                <div className={styles['suggestion-box']}>
                  {suggestions?.map((suggestion, index) => (
                    <button
                      className={styles['suggestion-text']}
                      key={index}
                      onClick={() => onClickSuggestionText(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      );
    default:
      return assertNever(width);
  }
};
